// AM Advertising Home — spine indexing + window math. PURE (no I/O, no clock).
//
// Everything on the page derives from SpineDay collections through these
// helpers, so "what did this account's channel do over [from..to]" has one
// implementation.

import type { AdChannel, SpineDay } from '../../types/advertisingHome.js';
import type { CurrencyAmount } from '../../types/reporting.js';

/** accountId → channel → rows (any order; window fns filter by date). */
export type SpineIndex = Map<string, Map<AdChannel, SpineDay[]>>;

export function indexSpine(rows: readonly SpineDay[]): SpineIndex {
  const index: SpineIndex = new Map();
  for (const row of rows) {
    let byChannel = index.get(row.accountId);
    if (!byChannel) {
      byChannel = new Map();
      index.set(row.accountId, byChannel);
    }
    const list = byChannel.get(row.channel);
    if (list) list.push(row);
    else byChannel.set(row.channel, [row]);
  }
  return index;
}

export function channelDays(
  index: SpineIndex,
  accountId: string,
  channel: AdChannel,
): SpineDay[] {
  return index.get(accountId)?.get(channel) ?? [];
}

/**
 * Date arithmetic on YYYY-MM-DD strings (UTC-based — no local-timezone drift).
 */
export function addDaysYmd(date: string, n: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** First day of `date`'s month. */
export function monthStartOf(date: string): string {
  return `${date.slice(0, 8)}01`;
}

/** Day-of-month (1-based) of a YYYY-MM-DD. */
export function dayOfMonth(date: string): number {
  return Number(date.slice(8, 10));
}

/** Days in `date`'s month. */
export function daysInMonthOf(date: string): number {
  const y = Number(date.slice(0, 4));
  const m = Number(date.slice(5, 7)); // 1-based
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

export interface WindowTotals {
  /**
   * Spend summed ACROSS currencies — safe for guard floors and per-account CPL
   * because one account-channel is one ad account = one native currency in
   * practice; the per-currency splits below are what any display-edge
   * conversion or per-currency render reads.
   */
  spend: number;
  leads: number;
  spendByCurrency: CurrencyAmount[];
  leadsByCurrency: CurrencyAmount[];
  /** The highest-spend currency in the window; null when nothing spent. */
  currency: string | null;
}

const r2 = (n: number): number => Math.round(n * 100) / 100;

/** Sum a channel's rows over [from..to] inclusive (total-grain rows excluded). */
export function sumWindow(
  days: readonly SpineDay[],
  from: string,
  to: string,
): WindowTotals {
  const spendBy = new Map<string, number>();
  const leadsBy = new Map<string, number>();
  let spend = 0;
  let leads = 0;
  for (const d of days) {
    if (d.date === null || d.date < from || d.date > to) continue;
    spend += d.spend;
    leads += d.leads;
    spendBy.set(d.currency, (spendBy.get(d.currency) ?? 0) + d.spend);
    leadsBy.set(d.currency, (leadsBy.get(d.currency) ?? 0) + d.leads);
  }
  let currency: string | null = null;
  let max = -Infinity;
  for (const [c, amount] of spendBy) {
    if (amount > max) {
      max = amount;
      currency = c;
    }
  }
  const toAmounts = (m: Map<string, number>): CurrencyAmount[] =>
    [...m.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([c, amount]) => ({ currency: c, amount: r2(amount) }));
  return {
    spend: r2(spend),
    leads: r2(leads),
    spendByCurrency: toAmounts(spendBy),
    leadsByCurrency: toAmounts(leadsBy),
    currency,
  };
}

/** Total-grain rows (date null) summed — the comparison-window read. */
export function sumTotals(days: readonly SpineDay[]): WindowTotals {
  // Total-grain rows carry date null; reuse sumWindow's fold with a pass-all
  // range by temporarily treating null dates as in-window.
  const asDaily = days.map((d) => (d.date === null ? { ...d, date: '0000-01-01' } : d));
  return sumWindow(asDaily, '0000-01-01', '9999-12-31');
}

/**
 * Spend on one specific day (0 when the day has no rows).
 *
 * ⚠️ 0 IS AMBIGUOUS HERE and callers that care must pair it with
 * `hasRowOn`: a row saying zero is a MEASURED zero, while no row at all means
 * either "this account had no delivery that day" or "this account's figures for
 * that day have not landed yet". The pull writes rows 1:1 from Zernio's
 * sparse-active-days `daily[]` series and never synthesises a zero, so both
 * produce the same absence — and nothing in the data separates them. ⚠️ The
 * no-spend rule therefore REPORTS the difference in its wording and does NOT
 * act on it; see the reverted-suppression note in `alerts.ts`.
 */
export function spendOn(days: readonly SpineDay[], date: string): number {
  let n = 0;
  for (const d of days) if (d.date === date) n += d.spend;
  return r2(n);
}

/** Does this channel have ANY row on that day? The absence-vs-zero test. */
export function hasRowOn(days: readonly SpineDay[], date: string): boolean {
  for (const d of days) if (d.date === date) return true;
  return false;
}

/** The earlier of two YYYY-MM-DD dates (they sort lexicographically). */
export function minYmd(a: string, b: string): string {
  return a < b ? a : b;
}

/** CPL from window totals — null when it cannot be computed (no leads). */
export function windowCpl(totals: WindowTotals): number | null {
  if (totals.leads <= 0 || totals.spend <= 0) return null;
  return totals.spend / totals.leads;
}
